import { tr } from '@/lib/i18n-core';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { get, post } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn, dateOnly, dateTime, humanize } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, SkeletonText, Input, Textarea } from '@/components/ui/primitives';
import { statusLabel } from '@/components/ui/badge';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { LabBenchAmendDialog } from './LabBenchAmendDialog';
import { LabBenchCriticalDialog } from './LabBenchCriticalDialog';
import { apiMessage, hasDetailPath } from './LabBenchErrors';
import { verifiableItems } from './bench-groups';
import {
  LOCKED,
  NOT_YET,
  ResultInput,
  analyserChange,
  buildEntry,
  flagFor,
  parseNumeric,
  seedDraft,
  type Draft,
  type Sheet,
  type SheetItem,
} from './LabBenchResultInput';

const MIN_QC_REASON = 5;

/**
 * The result sheet: one order, every test on it, filled in like a form.
 *
 * A CBC is twelve values; asking for twelve dialogs is how results get typed
 * against the wrong line. Here the whole requisition is one screen, Enter
 * moves down, the flag shows as you type, and the previous value sits beside
 * the reference so a mislabelled tube is caught before it is saved.
 */
export function OrderResultsSheet({
  orderId,
  focusItemId,
  onClose,
  onSaved,
}: {
  orderId: string | null;
  focusItemId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, user } = useAuth();
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [confirmCritical, setConfirmCritical] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qcOverrideReason, setQcOverrideReason] = useState('');
  const [qcDemanded, setQcDemanded] = useState(false);
  const [amendingId, setAmendingId] = useState<string | null>(null);
  const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);
  const [releaseHeld, setReleaseHeld] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');
  const focused = useRef(false);

  const sheet = useQuery({
    queryKey: ['lab-entry', orderId],
    queryFn: () => get<Sheet>(`/lab/orders/${orderId}/entry`),
    enabled: Boolean(orderId),
  });

  // Seed the drafts from what is already on the order, once per sheet.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (sheet.data && !sheet.isFetching && seededFor !== sheet.data.order.id) {
    setSeededFor(sheet.data.order.id);
    setDrafts(Object.fromEntries(sheet.data.items.map((item) => [item.id, seedDraft(item)])));
    setConfirmCritical(false);
    setQcOverrideReason('');
    setQcDemanded(false);
    focused.current = false;
  }

  useEffect(() => {
    if (!sheet.data || focused.current) return;
    const target = document.querySelector<HTMLElement>(
      focusItemId ? `[data-entry-item="${focusItemId}"]` : '[data-entry-item]:not([disabled])',
    );
    if (target) {
      target.focus();
      focused.current = true;
    }
  }, [sheet.data, focusItemId, seededFor]);

  const items = sheet.data?.items ?? [];
  const gate = sheet.data?.qcGate ?? { closed: [], canOverride: false };
  const closedTests = new Set(gate.closed.map((closure) => closure.testId));
  const qcClosed = (item: SheetItem) => closedTests.has(item.test.id);
  const blockedByQc = (item: SheetItem) => qcClosed(item) && !gate.canOverride;
  const overrideMode = sheet.data?.analyserOverride;
  const isVerifier = can('lab.result.verify');
  const analyserLocked = (item: SheetItem) => overrideMode === 'verifier_only' && !isVerifier && item.latestResult?.analyserValue != null;
  const editable = (item: SheetItem) => !LOCKED.has(item.status) && !NOT_YET.has(item.status) && !blockedByQc(item) && !analyserLocked(item);

  const changed = items.filter((item) => {
    if (!editable(item)) return false;
    const draft = drafts[item.id];
    if (!draft) return false;
    if (item.test.resultType === 'culture') {
      return draft.organism.trim() !== '' && (draft.organism !== item.latestResult?.culture?.organism || draft.comment.trim() !== '' || JSON.stringify(draft.sensitivities) !== JSON.stringify(item.latestResult?.culture?.sensitivities ?? []));
    }
    return draft.value.trim() !== '' && (draft.value !== item.latestResult?.value || draft.comment.trim() !== '');
  });

  const criticals = changed.filter((item) => flagFor(item, drafts[item.id]?.value ?? '').startsWith('critical'));
  const needsFlag = changed.filter((item) => {
    const draft = drafts[item.id];
    return item.test.resultType === 'numeric' && draft && flagFor(item, draft.value) === 'no_range' && !draft.flag && draft.comment.trim() === '';
  });
  const reasonFor = (item: SheetItem) => {
    const change = analyserChange(item, drafts[item.id], overrideMode);
    return change === 'reason' || change === 'approval' || change === 'verifier';
  };
  const missingReason = changed.filter((item) => reasonFor(item) && (drafts[item.id]?.overrideReason.trim().length ?? 0) < 3);
  const overrideNeeded = qcDemanded || (gate.canOverride && changed.some(qcClosed));
  const overrideReady = !overrideNeeded || qcOverrideReason.trim().length >= MIN_QC_REASON;
  const showOverride = gate.canOverride && (gate.closed.length > 0 || qcDemanded);

  const save = useMutation({
    mutationFn: () =>
      post<{ entered: { orderItemId: string; flag: string; critical: boolean }[]; reflexed: { from: string; added: string }[] }>(
        '/lab/results',
        {
          results: changed.map((item) => buildEntry(item, drafts[item.id])),
          ...(overrideNeeded ? { qcOverrideReason: qcOverrideReason.trim() } : {}),
        },
      ),
    onSuccess: (result) => {
      const critical = result.entered.filter((e) => e.critical).length;
      if (critical > 0) {
        toast.warning(`${critical} critical value${critical === 1 ? '' : 's'} recorded`, tr('lab', 'the_ordering_doctor_has_been_notified'));
      } else {
        toast.success(`${result.entered.length} result${result.entered.length === 1 ? '' : 's'} saved`);
      }
      if (result.reflexed.length) {
        toast.success(tr('lab', 'reflex_tests_added'), result.reflexed.map((r) => `${r.added} (from ${r.from})`).join(', '));
      }
      onSaved();
    },
    onError: (caught) => {
      setError(apiMessage(caught));
      setConfirmCritical(false);
      if (hasDetailPath(caught, 'qcOverrideReason')) setQcDemanded(true);
      void queryClient.invalidateQueries({ queryKey: ['lab-entry', orderId] });
    },
  });

  const submit = () => {
    setError(null);
    if (criticals.length > 0 && !confirmCritical) {
      setConfirmCritical(true);
      return;
    }
    save.mutate();
  };

  const setDraft = (id: string, patch: Partial<Draft>) =>
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));

  /** Enter behaves like Tab on a bench keyboard: down to the next result box. */
  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' || (event.target as HTMLElement).tagName === 'TEXTAREA') return;
    event.preventDefault();
    const inputs = [...document.querySelectorAll<HTMLElement>('[data-entry-item]:not([disabled])')];
    const index = inputs.indexOf(event.currentTarget as HTMLElement);
    const next = inputs[index + 1];
    if (next) next.focus();
    else if (changed.length > 0) submit();
  };

  const afterAmend = async (itemId: string) => {
    setAmendingId(null);
    void queryClient.invalidateQueries({ queryKey: ['lab-worklist'] });
    const fresh = await sheet.refetch();
    const updated = fresh.data?.items.find((row) => row.id === itemId);
    if (updated) setDrafts((current) => ({ ...current, [updated.id]: seedDraft(updated) }));
  };

  const patient = sheet.data?.patient;
  const amendingItem = items.find((item) => item.id === amendingId);
  const acknowledgingItem = items.find((item) => item.id === acknowledgingId);
  const nestedOpen = Boolean(amendingItem || acknowledgingItem);
  const mayAmend = can('lab.result.amend');
  const mayAcknowledge = can('lab.critical.acknowledge', 'lab.result.verify');
  const signable = verifiableItems(items, { separateVerifier: sheet.data?.separateVerifier === true, userId: user?.id ?? null });
  const mayVerify = can('lab.result.verify') && changed.length === 0 && signable.ready.length > 0;

  const signOff = useMutation({
    mutationFn: ({ action, overrideReason }: { action: 'verify' | 'release'; overrideReason?: string }) =>
      post('/lab/verify', { orderItemIds: signable.ready.map((item) => item.id), action, overrideReason }),
    onSuccess: (_result, variables) => {
      toast.success(variables.action === 'release' ? tr('lab', 'order_verified_and_released') : tr('lab', 'order_verified'));
      setReleaseHeld(false);
      onSaved();
    },
    onError: (caught, variables) => {
      if (hasDetailPath(caught, 'overrideReason') && variables.action === 'release') {
        setReleaseHeld(true);
        return;
      }
      setError(apiMessage(caught));
    },
  });

  return (
    <>
      <Dialog
        open={Boolean(orderId)}
        onClose={nestedOpen ? () => undefined : onClose}
        wide
        title={sheet.data ? tr('lab', 'results_x', { number: sheet.data.order.number }) : tr('lab', 'results')}
        description={
          patient
            ? `${patient.fullName} · ${patient.mrn} · ${humanize(patient.sex)}${patient.dob ? ` · ${ageText(patient.dob)}` : ''}${sheet.data?.order.priority !== 'routine' ? ` · ${sheet.data?.order.priority.toUpperCase()}` : ''}`
            : undefined
        }
        footer={
          <>
            <span className="me-auto text-xs text-muted-foreground">
              {changed.length === 0 ? tr('lab', 'nothing_changed_yet') : `${changed.length} result${changed.length === 1 ? '' : 's'} to save`}
            </span>
            <Button variant="ghost" onClick={onClose}>
              {tr('lab', 'close')}
            </Button>
            {mayVerify && (
              <>
                <Button variant="outline" onClick={() => signOff.mutate({ action: 'verify' })} loading={signOff.isPending && signOff.variables?.action === 'verify'}>
                  {tr('lab', 'verify')} {signable.ready.length}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => signOff.mutate({ action: 'release', ...(releaseHeld ? { overrideReason: releaseReason.trim() } : {}) })}
                  loading={signOff.isPending && signOff.variables?.action === 'release'}
                  disabled={releaseHeld && releaseReason.trim().length < 5}
                >
                  {tr('lab', 'verify_and_release')} {signable.ready.length}
                </Button>
              </>
            )}
            <Button
              onClick={submit}
              loading={save.isPending}
              disabled={changed.length === 0 || !overrideReady || needsFlag.length > 0 || missingReason.length > 0}
              variant={confirmCritical ? 'destructive' : 'default'}
            >
              {confirmCritical ? tr('lab', 'save_with_critical_values') : tr('lab', 'save_results')}
            </Button>
          </>
        }
      >
        {sheet.isLoading && (
          <SkeletonText lines={3} />
        )}
        {sheet.isError && <Alert variant="destructive">{apiMessage(sheet.error)}</Alert>}
        {error && <Alert variant="destructive">{error}</Alert>}
        {can('lab.result.verify') && signable.enteredByMe.length > 0 && (
          <Alert className="mb-3">
            {signable.enteredByMe.map((item) => item.test.name).join(' · ')}{tr('lab', 'you_entered')} {signable.enteredByMe.length === 1 ? tr('lab', 'this_result') : tr('lab', 'these_results')}{tr('lab', 'so_a_colleague_must_verify')} {signable.enteredByMe.length === 1 ? tr('lab', 'it') : tr('lab', 'them')}.
          </Alert>
        )}
        {releaseHeld && (
          <div className="mb-3 rounded-md border border-warning/40 p-3">
            <p className="text-sm font-semibold">{tr('lab', 'release_with_an_unpaid_bill')}</p>
            <Field label={tr('lab', 'supervisor_override_reason')} required hint={tr('lab', 'at_least_5_characters')}>
              <Textarea rows={2} value={releaseReason} maxLength={300} onChange={(e) => setReleaseReason(e.target.value)} />
            </Field>
          </div>
        )}
        {gate.closed.length > 0 && (
          <Alert variant="destructive" title={tr('lab', 'quality_control_has_failed')} className="mb-3">
            <ul className="mt-1 list-disc space-y-0.5 ps-5">
              {gate.closed.map((closure) => (
                <li key={closure.testId}>{closure.message}</li>
              ))}
            </ul>
            {!gate.canOverride && (
              <p className="mt-2 font-medium">
                {tr('lab', 'results_for_these_tests_cannot_be_saved_here_a')}
              </p>
            )}
          </Alert>
        )}
        {showOverride && (
          <div className="mb-3 rounded-md border border-destructive/30 p-3">
            <p className="text-sm font-semibold">{tr('lab', 'override_qc_failure')}</p>
            <p className="mb-2 text-xs text-muted-foreground">
              {tr('lab', 'only_needed_when_you_save_results_for_the')}
            </p>
            <Field label={tr('lab', 'reason_for_reporting_while_qc_has_failed')} required={overrideNeeded} hint={tr('lab', 'at_least_x_characters', { MIN_QC_REASON: MIN_QC_REASON })}>
              <Textarea rows={2} value={qcOverrideReason} maxLength={300} onChange={(e) => setQcOverrideReason(e.target.value)} />
            </Field>
          </div>
        )}
        {missingReason.length > 0 && (
          <Alert variant="warning" title={tr('lab', 'analyser_values_changed')}>
            {missingReason.map((item) => item.test.name).join(' · ')}{tr('lab', 'say_why_each_analyser_value_is_being_changed')}
          </Alert>
        )}
        {needsFlag.length > 0 && (
          <Alert variant="warning" title={tr('lab', 'no_reference_range')}>
            {needsFlag.map((item) => item.test.name).join(' · ')}{tr('lab', 'no_reference_range_applies_to_this_patient_so')}
          </Alert>
        )}
        {confirmCritical && criticals.length > 0 && (
          <Alert variant="warning" title={tr('lab', 'critical_values')}>
            {criticals.map((item) => `${item.test.name}: ${drafts[item.id]?.value}`).join(' · ')}{tr('lab', 'the_ordering_doctor_will_be_notified_the_moment')}
          </Alert>
        )}
        {sheet.data?.order.clinicalNotes && (
          <p className="mb-3 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <span className="font-medium">{tr('lab', 'clinical_notes_2')}</span> {sheet.data.order.clinicalNotes}
          </p>
        )}

        {sheet.data && (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-start text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{tr('lab', 'test')}</th>
                  <th className="px-3 py-2">{tr('lab', 'result')}</th>
                  <th className="px-3 py-2">{tr('lab', 'unit')}</th>
                  <th className="px-3 py-2">{tr('lab', 'reference')}</th>
                  <th className="px-3 py-2">{tr('lab', 'previous')}</th>
                  <th className="px-3 py-2">{tr('lab', 'flag')}</th>
                  <th className="px-3 py-2">{tr('lab', 'comment')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const draft = drafts[item.id];
                  const locked = LOCKED.has(item.status);
                  const notYet = NOT_YET.has(item.status);
                  const blocked = blockedByQc(item);
                  const flag = draft ? flagFor(item, draft.value) : 'normal';
                  const delta = deltaFor(item, draft?.value ?? '');
                  const saved = item.latestResult;
                  return (
                    <tr
                      key={item.id}
                      className={cn(
                        'border-t align-top',
                        flag.startsWith('critical') && 'bg-destructive/5',
                        flag !== 'normal' && !flag.startsWith('critical') && 'bg-warning/5',
                      )}
                    >
                      <td className="px-3 py-2">
                        <p className="font-medium">{item.test.name}</p>
                        <p className="text-xs text-muted-foreground">
                          <span className="font-mono">{item.test.code}</span>
                          {item.panel && <> · {item.panel.code}</>}
                          {item.accessionNo && <> · {item.accessionNo}</>}
                        </p>
                        {locked && <Badge variant="muted" className="mt-1">{statusLabel(item.status)}</Badge>}
                        {item.resultHidden && <p className="mt-1 text-xs text-muted-foreground">{tr('lab', 'awaiting_verification')}</p>}
                        {notYet && <Badge variant="muted" className="mt-1">{tr('lab', 'not_collected')}</Badge>}
                        {qcClosed(item) && !locked && !notYet && <Badge variant="destructive" className="mt-1">{tr('lab', 'qc_failed')}</Badge>}
                        {saved?.source === 'analyser' && <Badge variant="secondary" className="mt-1">{tr('lab', 'analyser')}</Badge>}
                        {saved?.overridePending && <Badge variant="warning" className="mt-1">{tr('lab', 'change_waiting_for_approval')}</Badge>}
                        {saved?.restored && <p className="mt-1 text-xs text-muted-foreground">{tr('lab', 'analyser_value_restored')}</p>}
                        {saved?.analyserValue != null && saved.analyserValue !== saved.value && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {tr('lab', 'analyser_sent')} {saved.analyserValue}
                            {saved.overrideReason ? ` · ${saved.overrideReason}` : ''}
                          </p>
                        )}
                        {mayAmend && (item.status === 'verified' || item.status === 'released') && (
                          <Button size="sm" variant="outline" className="mt-1 block h-7 text-xs" onClick={() => setAmendingId(item.id)}>
                            {tr('lab', 'amend_result')}
                          </Button>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {draft && (
                          <ResultInput
                            item={item}
                            draft={draft}
                            disabled={locked || notYet || blocked || analyserLocked(item)}
                            onChange={(patch) => setDraft(item.id, patch)}
                            onKeyDown={onKeyDown}
                          />
                        )}
                        {item.status === 'received' && saved?.comment?.includes('Sent back:') && (
                          <div className="mt-1 rounded border border-warning/30 bg-warning/10 px-2 py-1 text-xs text-warning">
                            {saved.comment.slice(saved.comment.indexOf('Sent back:'))}
                          </div>
                        )}
                        {saved?.isAmended && (
                          <div className="mt-1 rounded border border-warning/30 bg-warning/10 px-2 py-1 text-xs text-warning">
                            <p className="font-medium">{tr('lab', 'amended_previously')} {saved.previousValue ?? '—'}</p>
                            {saved.amendReason && <p>{tr('lab', 'reason_2')} {saved.amendReason}</p>}
                          </div>
                        )}
                        {saved?.isCritical && !saved.criticalAckAt && (
                          <div className="mt-1 flex flex-wrap items-center gap-2 rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs text-destructive">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            <span className="font-semibold">{tr('lab', 'critical_not_yet_acknowledged')}</span>
                            {mayAcknowledge && (
                              <Button size="sm" variant="destructive" className="h-6 px-2 text-[11px]" onClick={() => setAcknowledgingId(item.id)}>
                                {tr('lab', 'acknowledge')}
                              </Button>
                            )}
                          </div>
                        )}
                        {saved?.isCritical && saved.criticalAckAt && (
                          <p className="mt-1 text-xs text-success">
                            {tr('lab', 'acknowledged_by')} {saved.criticalAckTo ?? tr('lab', 'a_clinician')} · {dateTime(saved.criticalAckAt)}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{item.test.unit ?? ''}</td>
                      <td className="px-3 py-2 text-muted-foreground">{item.reference?.text || (item.test.resultType === 'numeric' ? tr('lab', 'no_range') : '—')}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {item.previous ? (
                          <>
                            {item.previous.value}
                            <span className="block">{dateOnly(item.previous.enteredAt)}</span>
                            {delta !== null && Math.abs(delta) > 50 && (
                              <span className="mt-0.5 inline-flex items-center gap-1 text-warning">
                                <AlertTriangle className="h-3 w-3" /> {delta > 0 ? '+' : ''}
                                {delta}%
                              </span>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {draft?.value.trim() && item.test.resultType === 'numeric' && (
                          <Badge variant={flag.startsWith('critical') ? 'destructive' : flag === 'normal' ? 'success' : 'warning'}>
                            {humanize(flag)}
                          </Badge>
                        )}
                        {draft?.value.trim() && item.test.resultType === 'numeric' && flag === 'no_range' && !locked && !notYet && (
                          <ChoiceButtons<string>
                            layout="chips"
                            size="sm"
                            className="mt-1"
                            aria-label={tr('lab', 'flag_for_x', { name: item.test.name })}
                            value={draft.flag}
                            onChange={(value) => setDraft(item.id, { flag: value })}
                            options={[
                              { value: 'normal', label: tr('lab', 'normal') },
                              { value: 'low', label: tr('lab', 'low') },
                              { value: 'high', label: tr('lab', 'high') },
                              { value: 'abnormal', label: tr('lab', 'abnormal') },
                            ]}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {draft && reasonFor(item) && (
                          <Input
                            className="mb-1 h-8 min-w-[10rem] border-warning text-xs"
                            aria-label={tr('lab', 'why_change_the_analyser_value_for_x', { name: item.test.name })}
                            placeholder={analyserChange(item, draft, overrideMode) === 'approval' && !isVerifier ? tr('lab', 'why_change_it_needs_approval') : tr('lab', 'why_change_the_analyser_value')}
                            value={draft.overrideReason}
                            maxLength={300}
                            onChange={(e) => setDraft(item.id, { overrideReason: e.target.value })}
                          />
                        )}
                        {draft && (
                          <Input
                            className="h-8 min-w-[10rem] text-xs"
                            value={draft.comment}
                            placeholder={item.latestResult ? tr('lab', 'reason_for_amendment') : ''}
                            disabled={locked || notYet || blocked}
                            onChange={(e) => setDraft(item.id, { comment: e.target.value })}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {items.some((item) => item.latestResult && editable(item)) && (
          <p className="mt-3 text-xs text-muted-foreground">
            {tr('lab', 'changing_a_value_that_is_already_entered')}
          </p>
        )}
      </Dialog>

      {amendingItem && (
        <LabBenchAmendDialog
          item={amendingItem}
          onClose={() => setAmendingId(null)}
          onAmended={(result) => void afterAmend(result.orderItemId)}
        />
      )}
      {acknowledgingItem && (
        <LabBenchCriticalDialog
          item={acknowledgingItem}
          onClose={() => setAcknowledgingId(null)}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ['lab-entry', orderId] })}
        />
      )}
    </>
  );
}

function deltaFor(item: SheetItem, raw: string): number | null {
  const current = parseNumeric(raw);
  const previous = item.previous?.numericValue ?? null;
  if (current === null || previous === null || previous === 0) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

function ageText(dob: string): string {
  const days = Math.floor((Date.now() - new Date(dob).getTime()) / 86_400_000);
  if (days < 31) return `${days} d`;
  if (days < 365 * 2) return `${Math.floor(days / 30.44)} mo`;
  return `${Math.floor(days / 365.25)} y`;
}
